import { type UnwindingPositionStore, useUnwindingStore } from "@/hooks";
import { IconButton, Skeleton } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useUnwindingPositionItem } from "../positions/useUnwindingPositionItem";
import { formatTokenAmountForDisplay } from "@/utils";
import { useInterval } from "usehooks-ts";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../shared";

const calculateRemainingTime = (endDateMs: number, nowMs: number) => {
    const diff = endDateMs - nowMs;

    if (diff <= 0) {
        return "Ended";
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / 1000 / 60) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    const pad = (num: number) => num.toString().padStart(2, "0");

    return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const CountdownToEndDate = ({
    endDateMs,
    onEndedChange,
}: { endDateMs: number; onEndedChange: (isEnded: boolean) => void }) => {
    const [isEnded, setIsEnded] = useState(() => Date.now() >= endDateMs);
    const [remainingTime, setRemainingTime] = useState("");
    const updateCountdown = useCallback(() => {
        const nowMs = Date.now();
        const ended = nowMs >= endDateMs;
        setIsEnded(ended);
        onEndedChange(ended);
        setRemainingTime(calculateRemainingTime(endDateMs, nowMs));
    }, [endDateMs, onEndedChange]);

    useEffect(() => {
        updateCountdown();
    }, [updateCountdown]);

    useInterval(updateCountdown, isEnded ? null : 1000);

    if (!remainingTime) {
        return <Skeleton className="w-[110px]" />;
    }

    return (
        <div className="w-[110px] font-mono text-[12px] text-dark/60">{isEnded ? "--:--:--:--" : remainingTime}</div>
    );
};

const QuickAccessUnwindingPosition = ({ position }: { position: UnwindingPositionStore }) => {
    const { handleCancelExit, handleFinalizeWithdrawal } = useUnwindingPositionItem(position);
    const unwindingBalance = Number(position.lockedBalance) + Number(position.rewardsEarned);
    const endDateMs = useMemo(() => Date.parse(position.endDate), [position.endDate]);
    const [canWithdraw, setCanWithdraw] = useState(() => !Number.isNaN(endDateMs) && Date.now() >= endDateMs);

    useEffect(() => {
        setCanWithdraw(!Number.isNaN(endDateMs) && Date.now() >= endDateMs);
    }, [endDateMs]);

    const action = useMemo(() => {
        if (!canWithdraw) {
            return (
                <Button
                    variant="ghost"
                    onClick={handleCancelExit}
                    className="cursor-pointer text-red-500 whitespace-nowrap flex justify-end items-center min-w-[80px] text-[12px] font-medium hover:text-red-600"
                >
                    Cancel
                </Button>
            );
        }
        return (
            <Button
                variant="ghost"
                onClick={handleFinalizeWithdrawal}
                className="cursor-pointer text-primary whitespace-nowrap flex justify-end items-center min-w-[80px] text-[12px] font-medium hover:text-primary/80"
            >
                Withdraw
            </Button>
        );
    }, [canWithdraw, handleCancelExit, handleFinalizeWithdrawal]);

    return (
        <div className="min-w-xs border-b border-light/70 last:border-b-0">
            <div className="grid grid-cols-4 items-center gap-4 text-sm">
                <span className="font-mono text-dark">liUSD-{position.lockDuration}</span>
                <span className="font-mono text-dark">${formatTokenAmountForDisplay(unwindingBalance)}</span>
                <span className="font-mono text-start">
                    {!Number.isNaN(endDateMs) && (
                        <CountdownToEndDate endDateMs={endDateMs} onEndedChange={setCanWithdraw} />
                    )}
                </span>
                <span className="flex justify-end">{action}</span>
            </div>
        </div>
    );
};

export const QuickAccessUnwinding = memo(function QuickAccessUnwinding() {
    const { unwindingPositions, isLoading } = useUnwindingStore();

    const [hidden, setHidden] = useState(false);

    if (hidden || isLoading || unwindingPositions?.length === 0) {
        return null;
    }

    return (
        <div className="quick-access-unwinding fixed hidden 2xl:block bottom-[92px] right-2 z-50 min-w-sm max-w-md overflow-hidden rounded-xl border-[1px] border-primary/10 bg-card shadow-lg">
            <div className="flex items-center justify-between px-4 py-2">
                <span className="text-primary text-[16px] leading-[22px] font-medium tracking-[0.2px]">
                    Unwinding Positions (<span className="font-mono">{unwindingPositions?.length}</span>)
                </span>
                <IconButton
                    size="small"
                    onClick={() => setHidden(true)}
                    className="text-primary/70 hover:text-dark transition-all"
                >
                    <XMarkIcon className="w-5 h-5 " />
                </IconButton>
            </div>
            <div className="flex flex-col bg-card px-4 pb-0">
                {unwindingPositions?.map((position) => (
                    <QuickAccessUnwindingPosition position={position} key={position.id} />
                ))}
            </div>
        </div>
    );
});

QuickAccessUnwinding.displayName = "QuickAccessUnwinding";
